import { Link } from "react-router-dom";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";
import { ArrowRight, CheckCircle, Laptop, Bell, ShieldCheck, Mail, Phone } from "lucide-react";

const ASPETTI = [
  { icon: ShieldCheck, label: "Aspetti normativi e contrattuali" },
  { icon: ShieldCheck, label: "Sicurezza sul lavoro" },
  { icon: Laptop,      label: "Utilizzo degli strumenti di lavoro e diritto alla disconnessione" },
  { icon: Bell,        label: "Controllo a distanza e comunicazioni obbligatorie" },
];

export default function FlessibilitaOrganizzativaProposta2Page() {
  return (
    <main id="main-content" data-testid="flessibilita-proposta2-page">

      {/* Hero */}
      <section className="relative h-[280px] md:h-[340px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=60&auto=format"
          alt="Flessibilità organizzativa"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
            Area Lavoro e Welfare
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Flessibilità organizzativa
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug mb-5">
                Flessibilità organizzativa con il supporto di Confcommercio Roma: la tua scelta agile!
              </h2>
              <p className="text-lg text-[var(--confcom-text-secondary)] leading-relaxed">
                Vuoi attrarre talenti ed essere competitivo e all'avanguardia sull'organizzazione del lavoro?{" "}
                <strong className="text-[var(--confcom-text-primary)]">Confcommercio Roma ha la soluzione per te!</strong>
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80&auto=format"
                alt="Team in smart working"
                className="w-full h-64 object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Smart Working */}
      <section className="py-14 bg-[#EBF5FF]" id="smart-working">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            {/* Immagine */}
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img
                src="https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800&q=80&auto=format"
                alt="Smart working"
                className="w-full h-72 lg:h-96 object-cover"
                loading="lazy"
              />
            </div>
            {/* Testo */}
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
                Smart Working
              </span>
              <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-5">
                Il lavoro agile per la tua impresa
              </h2>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-5">
                Con lo smart working, disciplinato dalla{" "}
                <strong className="text-[var(--confcom-text-primary)]">legge n. 81/2017</strong>, puoi incrementare la tua competitività e facilitare la{" "}
                <strong className="text-[var(--confcom-text-primary)]">conciliazione</strong> dei tempi di vita e di lavoro delle tue risorse, consentendo lo svolgimento della{" "}
                <strong className="text-[var(--confcom-text-primary)]">prestazione lavorativa</strong> anche all'esterno dell'azienda e senza una postazione fissa.
              </p>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-6">
                Confcommercio Roma offre{" "}
                <strong className="text-[var(--confcom-text-primary)]">supporto alle imprese</strong> con un'assistenza all'azienda a 360° sugli aspetti:
              </p>
              {/* Aspetti */}
              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {ASPETTI.map((a) => (
                  <div key={a.label} className="flex items-start gap-3 bg-white rounded-xl border border-[var(--confcom-border)] px-4 py-3">
                    <CheckCircle size={16} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-sm text-[var(--confcom-text-secondary)] leading-snug">{a.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                Inoltre, le imprese possono entrare in contatto con i partner di Confcommercio Roma che progettano la{" "}
                <strong className="text-[var(--confcom-text-primary)]">rivisitazione e riorganizzazione</strong> degli spazi aziendali nell'ambito dello smart working, con il contestuale svolgimento di corsi formativi del personale interessato. Tramite{" "}
                <strong className="text-[var(--confcom-text-primary)]">Ebit Lazio</strong>, le aziende interessate potranno anche accedere alla formazione gratuita sui temi del lavoro agile.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Flessibilità oraria */}
      <section className="py-14 bg-white" id="flessibilita-oraria">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
                Contrattazione territoriale
              </span>
              <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-5">
                Ulteriori forme di flessibilità organizzativa
              </h2>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-6">
                Oltre allo smart working, Confcommercio Roma promuove ulteriori forme di flessibilità organizzativa. Tra queste, il{" "}
                <strong className="text-[var(--confcom-text-primary)]">Contratto Integrativo Regionale del Terziario</strong>, firmato da Confcommercio Roma, prevede:
              </p>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle size={17} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-sm text-[var(--confcom-text-secondary)] leading-snug">
                    La <strong className="text-[var(--confcom-text-primary)]">flessibilità oraria settimanale</strong> con la possibilità di riduzione dell'orario fino a <strong className="text-[var(--confcom-text-primary)]">36 ore settimanali</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={17} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-sm text-[var(--confcom-text-secondary)] leading-snug">
                    L'<strong className="text-[var(--confcom-text-primary)]">assorbimento dei permessi</strong> per un periodo nell'anno massimo di <strong className="text-[var(--confcom-text-primary)]">18 settimane</strong>
                  </span>
                </li>
              </ul>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                Queste misure consentono alle imprese di migliorare la{" "}
                <strong className="text-[var(--confcom-text-primary)]">gestione del personale</strong> e ai lavoratori di conciliare meglio vita privata e professionale.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80&auto=format"
                alt="Flessibilità oraria"
                className="w-full h-72 lg:h-96 object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/cta-background.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.88)" }} />
        </div>
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Per maggiori informazioni</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mt-3 mb-8">Contattaci</h2>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-left" data-testid="flessibilita-organizzativa-contatti">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-white/80">
                <Mail size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="mailto:sindacale@confcommercioroma.it" className="hover:text-white transition-colors">sindacale@confcommercioroma.it</a>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Phone size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="tel:+390668437221" className="hover:text-white transition-colors">Tel: 06 68437 221-290</a>
              </div>
            </div>
          </div>

          <Link
            to="/chi-siamo/struttura-tecnica#ufficio-lavoro-e-welfare"
            data-testid="flessibilita-organizzativa-scopri-team"
            className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-6 py-3 rounded-md font-semibold hover:bg-white/90 transition-colors text-sm mt-8"
          >
            Scopri il Team <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>

    </main>
  );
}
